import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import type { CalibrationHoldReason, CalibrationSession, DayRestriction, DripLayout, NewSeedProgram, PrecipRateHint, RunRecord, ScheduleConfig, ScheduleMode, Zone, ZoneDriftField } from '@irrigation/core';
import {
  activeRuns,
  allowedDepletionIn,
  DEFAULT_PRECIP_RATE_IN_PER_HR,
  DRAINAGE_HOURS,
  DRIP_LAYOUTS,
  DRIP_LAYOUT_PRESETS,
  moistureBoundsPct,
  newSeedActive,
  newSeedDailyDepthIn,
  newSeedDailyMinutes,
  NOZZLE_TYPES,
  PLANT_TYPES,
  SLOPES,
  SOIL_TYPES,
  SUN_EXPOSURES,
} from '@irrigation/core';
import { revealRow, rowDomId, useAttentionTarget, type ReviewFlow } from '../lib/attention.js';
import { mirrorDependentSeedBlock, newSeedLive } from '../lib/newSeed.js';
import { calibrationRunMinutes, calibrationRuntimeCap, sessionEstimate } from '../lib/estimate.js';
import { batchSummary, batchToast } from '../lib/runZones.js';
import { api, type SensorSummary, type ZoneWithSoil } from '../api.js';
import type { MenuItem } from '../components/ui.js';
import {
  Banner,
  Button,
  Card,
  CardSkeleton,
  EmptyState,
  Field,
  Menu,
  Modal,
  Pill,
  Section,
  Select,
  Skeleton,
  Switch,
  TextInput,
  useToast,
} from '../components/ui.js';
import { SoilHistoryChart } from '../components/SoilHistoryChart.js';
import {
  describeDays,
  DRIFT_FIELD_LABELS,
  DRIP_LAYOUT_LABELS,
  errorMessage,
  clockTime,
  fmtInches,
  fmtMinutes,
  fmtPct,
  fmtTime,
  friendlyDate,
  isoDatePlus,
  NOZZLE_LABELS,
  PLANT_LABELS,
  recentDates,
  relativeTime,
  shortDate,
  SLOPE_LABELS,
  SOIL_LABELS,
  SUN_LABELS,
  toIsoDate,
  WEEKDAY_LABELS,
} from '../lib/format.js';
import { useLoad } from '../lib/useLoad.js';

async function loadZonesData(): Promise<{
  zones: ZoneWithSoil[];
  sensors: SensorSummary[];
  config: ScheduleConfig;
  runs: RunRecord[];
}> {
  const [zones, sensors, config, runs] = await Promise.all([
    api.listZones(),
    api.listSensors().catch(() => []),
    api.getConfig(),
    // Two consumers: whether anything is running right now (this is the page
    // you're on after starting a manual run, so Stop has to be reachable
    // here), and each zone's latest metered flow for the card's flow tag —
    // two weeks covers the ET cadence between waterings.
    api.listRuns(recentDates(14)).catch(() => []),
  ]);
  return { zones, sensors, config, runs };
}

export function Zones() {
  const { data, loading, error, reload } = useLoad(loadZonesData);
  const [editing, setEditing] = useState<Zone | null>(null);
  const [deleting, setDeleting] = useState<Zone | null>(null);
  // The quick-run picker, open with an optional pre-checked zone. Null means
  // closed; `{}` means open with nothing chosen (the page-level button).
  const [runPicker, setRunPicker] = useState<{ initial?: Zone } | null>(null);
  const [reviewing, setReviewing] = useState<Zone | null>(null);
  const [flowReview, setFlowReview] = useState<Zone | null>(null);
  const [calibrating, setCalibrating] = useState<Zone | null>(null);
  const [checkingCal, setCheckingCal] = useState<ZoneWithSoil | null>(null);
  const [startingCal, setStartingCal] = useState<Zone | null>(null);
  const [history, setHistory] = useState<ZoneWithSoil | null>(null);
  const [stopping, setStopping] = useState(false);
  const toast = useToast();

  /*
    Arriving from the dashboard's "Needs attention" list. The item named a zone
    and, for drift and calibration suggestions, a review to open — so open it
    rather than leaving the user to spot the right card themselves.

    The row is revealed BEFORE the dialog opens, in that order deliberately:
    Modal records whatever had focus when it first renders, so focusing the card
    first is what gives Escape somewhere sensible to return to.

    The condition is re-checked against freshly loaded data because the link can
    be stale — the drift may already have been accepted in another tab, in which
    case the row alone is the honest answer.
  */
  const onAttention = useCallback(
    (id: string, review: ReviewFlow | undefined) => {
      const zone = (data?.zones ?? []).find((z) => z.id === id);
      if (!zone) return;
      revealRow('zones', id);
      if (review === 'drift' && zone.drift) setReviewing(zone);
      else if (review === 'calibration' && zone.calibrationSuggestion) setCalibrating(zone);
      else if (review === 'flow' && zone.flowAnomaly) setFlowReview(zone);
    },
    [data],
  );
  useAttentionTarget('zones', !!data, onAttention);

  const toggleEnabled = async (zone: Zone, enabled: boolean) => {
    try {
      const { soil: _soil, ...persisted } = zone as ZoneWithSoil;
      await api.updateZone(zone.id, { ...persisted, enabled });
      await reload();
      toast(enabled ? `${zone.name} enabled.` : `${zone.name} paused — it won't be scheduled.`);
    } catch (e) {
      toast(errorMessage(e, 'Failed to update zone.'), 'error');
    }
  };

  const dismissSession = async (zone: Zone) => {
    try {
      await api.dismissCalibrationSession(zone.id);
      await reload();
      toast('Calibration note dismissed.');
    } catch (e) {
      toast(errorMessage(e, 'Failed to dismiss.'), 'error');
    }
  };

  const dismissPrecip = async (zone: Zone) => {
    try {
      await api.dismissPrecipHint(zone.id);
      await reload();
      toast('Hint dismissed.');
    } catch (e) {
      toast(errorMessage(e, 'Failed to dismiss.'), 'error');
    }
  };

  const dismissDisagreement = async (zone: Zone) => {
    try {
      await api.dismissSensorDisagreement(zone.id);
      await reload();
      toast('Sensor note dismissed.');
    } catch (e) {
      toast(errorMessage(e, 'Failed to dismiss.'), 'error');
    }
  };

  if (loading) {
    return (
      <div className="stack">
        <CardSkeleton lines={2} />
        <CardSkeleton lines={2} />
      </div>
    );
  }
  if (error) return <Banner tone="error">{error}</Banner>;

  const sensors = data?.sensors ?? [];
  // RUNNING is not "a valve is open": a new-seed misting record holds that
  // status for the whole local day, misting for a couple of minutes at a time.
  // `runActivity` is what knows the difference, so ask it rather than the
  // status field, and only lead with zones whose water is actually moving.
  const watering = activeRuns(data?.runs ?? [], Date.now()).filter((a) => a.phase === 'WATERING');
  const driftedZones = (data?.zones ?? []).filter((z) => z.drift);
  const zoneName = (id: string) =>
    (data?.zones ?? []).find((z) => z.id === id)?.name ?? 'a zone this app does not manage';
  const list = [...(data?.zones ?? [])].sort((a, b) => a.zoneNumber - b.zoneNumber);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Zones</h1>
          <div className="sub">Each zone's plant, soil, and nozzle details drive how much it gets watered.</div>
        </div>
        {/* Same guard the per-card "Run now" relies on: there is something to
            run. Without zones the list below is an empty state anyway. */}
        {list.length > 0 && (
          <Button variant="subtle" onClick={() => setRunPicker({})}>
            Run zones…
          </Button>
        )}
      </div>

      {watering.length > 0 && (
        <div className="status-hero live" role="status" aria-live="polite">
          <span className="dot" aria-hidden="true" />
          <div className="grow">
            <div className="title">Watering {watering.map((a) => zoneName(a.run.zoneId)).join(', ')}</div>
            <div className="detail">Started from this app or the controller.</div>
          </div>
          <Button
            variant="danger"
            busy={stopping}
            className="hero-stop"
            onClick={() => {
              setStopping(true);
              void api
                .stopWatering()
                .then(() => {
                  toast('Sent stop command to the controller.');
                  return reload();
                })
                .catch((e) => toast(errorMessage(e, 'Failed to stop watering.'), 'error'))
                .finally(() => setStopping(false));
            }}
          >
            Stop watering
          </Button>
        </div>
      )}

      {driftedZones.length > 1 && (
        <Banner
          tone="info"
          action={
            <Button variant="subtle" size="sm" onClick={() => setReviewing(driftedZones[0]!)}>
              Review changes
            </Button>
          }
        >
          Rachio settings changed on {driftedZones.length} zones: {driftedZones.map((z) => z.name).join(', ')}. Each
          zone's card also carries its own marker.
        </Banner>
      )}

      {list.length === 0 ? (
        <Card>
          <EmptyState
            icon="🌱"
            title="No zones yet"
            actions={
              <Link className="btn primary" to="/connect">
                Connect Rachio & import zones
              </Link>
            }
          >
            Connect your Rachio controller and import its zones — then tune each one here.
          </EmptyState>
        </Card>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="list" role="list" aria-label="Zones">
            {list.map((z) => (
              <ZoneRow
                key={z.id}
                zone={z}
                flow={latestCleanFlow(data?.runs ?? [], z.id)}
                sensor={sensors.find((s) => s.id === z.moistureSensorId)}
                targetName={z.mirrorZoneId ? list.find((t) => t.id === z.mirrorZoneId)?.name : undefined}
                onEdit={() => setEditing(z)}
                onDelete={() => setDeleting(z)}
                onRun={() => setRunPicker({ initial: z })}
                onReview={() => setReviewing(z)}
                onCalibrate={() => setCalibrating(z)}
                onFlowReview={() => setFlowReview(z)}
                onCheckCalibration={() => setCheckingCal(z)}
                onStartCalibration={() => setStartingCal(z)}
                onDismissSession={() => void dismissSession(z)}
                onDismissPrecip={() => void dismissPrecip(z)}
                onDismissDisagreement={() => void dismissDisagreement(z)}
                onHistory={() => setHistory(z)}
                onToggle={(enabled) => void toggleEnabled(z, enabled)}
              />
            ))}
          </div>
        </div>
      )}

      {editing && data && (
        <ZoneEditor
          zone={editing}
          sensors={sensors}
          zones={list}
          config={data.config}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
            toast('Zone saved.');
          }}
        />
      )}

      {deleting && (
        <DeleteConfirm
          zone={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            // Refresh the list BEFORE closing. Closing first leaves the card's
            // Delete button still mounted, so Modal restores focus to it and the
            // reload then removes it — dropping focus on <body>.
            await reload();
            setDeleting(null);
            toast(`${deleting.name} deleted.`);
          }}
        />
      )}

      {runPicker && (
        <RunZones
          zones={list}
          {...(runPicker.initial ? { initial: runPicker.initial } : {})}
          onClose={() => setRunPicker(null)}
          onStarted={(message) => {
            setRunPicker(null);
            void reload();
            toast(message);
          }}
        />
      )}

      {reviewing && (
        <DriftReview
          zone={reviewing}
          onClose={() => setReviewing(null)}
          onResolved={(message) => {
            setReviewing(null);
            void reload();
            toast(message);
          }}
        />
      )}

      {flowReview && (
        <FlowReview
          zone={flowReview}
          onClose={() => setFlowReview(null)}
          onResolved={(message) => {
            setFlowReview(null);
            void reload();
            toast(message);
          }}
        />
      )}

      {calibrating && (
        <CalibrationReview
          zone={calibrating}
          onClose={() => setCalibrating(null)}
          onResolved={(message) => {
            setCalibrating(null);
            void reload();
            toast(message);
          }}
        />
      )}

      {startingCal && data && (
        <StartCalibration
          zone={startingCal}
          config={data.config}
          onClose={() => setStartingCal(null)}
          onStarted={() => {
            const name = startingCal.name;
            setStartingCal(null);
            void reload();
            toast(`Calibrating "${name}" — the result appears after the soil drains.`);
          }}
        />
      )}

      {checkingCal && (
        <CalibrationCheck
          zone={checkingCal}
          onClose={() => setCheckingCal(null)}
          onResolved={(message) => {
            setCheckingCal(null);
            void reload();
            toast(message);
          }}
        />
      )}

      {history && <HistoryModal zone={history} onClose={() => setHistory(null)} />}
    </div>
  );
}

/** Fetches and shows the 30-day soil history chart for a zone. */
function HistoryModal({ zone, onClose }: { zone: ZoneWithSoil; onClose: () => void }) {
  const load = useCallback(
    () =>
      Promise.all([api.zoneHistory(zone.id, 30), api.listRuns(recentDates(30))]).then(([history, runs]) => ({
        ...history,
        runs: runs.filter((r) => r.zoneId === zone.id),
      })),
    [zone.id],
  );
  const { data, loading, error } = useLoad(load);

  return (
    <Modal title={`${zone.name} — soil history`} onClose={onClose}>
      {loading ? (
        <Skeleton height="200px" />
      ) : error || !data ? (
        <Banner tone="error">{error || 'Failed to load history.'}</Banner>
      ) : (
        <SoilHistoryChart zone={zone} snapshots={data.snapshots} readings={data.readings} runs={data.runs} />
      )}
    </Modal>
  );
}

/** Plain-language explanation for each inconclusive calibration outcome. */
const CALIBRATION_FAILURE_COPY: Record<NonNullable<CalibrationSession['failure']>, string> = {
  NO_RISE: 'The sensor barely moved — water may not be reaching the probe, or the run was too short. Check the valve and sprinkler, then try again.',
  NO_PLATEAU: 'The moisture level never settled — it was probably interrupted by more water (rain or another run). Try again on a dry stretch.',
  NO_DATA: 'No readings arrived during the run — check that the sensor is reporting, then try again.',
};

/**
 * The action clause for a precip-rate hint, keyed to its cause and the zone's
 * delivery lever. The three causes need opposite fixes, so the copy must too:
 * under-delivery says water more (the *right* way for this zone), runoff says
 * water less-but-more-often, over-delivery says the rate looks understated.
 */
function precipHintAction(hint: PrecipRateHint, zone: Zone): string {
  if (hint.cause === 'OVER_DELIVERY') {
    return 'That is more than intended — the application rate may be understated, so this zone could be over-watering. Confirm the rate and update the zone if needed.';
  }
  if (hint.cause === 'RUNOFF') {
    return 'Water is reaching the surface but draining away before the roots can store it. Longer runs will not help — split it into shorter, more frequent cycles so it can soak in.';
  }
  // UNDER_DELIVERY — genuinely short. Point at the knob that actually helps.
  if (hint.lever === 'RATE') {
    return 'The application rate may be overstated, so each run comes up short. Confirm it with a catch-cup test and lower it if needed — that lengthens the runs.';
  }
  if (zone.fixedRuntimeMinutes !== undefined) {
    return 'Water is not reaching the root zone in the amount intended. This zone runs for a fixed time, so increase its runtime to deliver more.';
  }
  return 'Water is not reaching the root zone in the amount intended. A catch-cup test is not meaningful for drip — instead lower this zone’s configured drip rate (its emitters/preset) so runs last longer, or pin a longer runtime.';
}

/**
 * The zone's most recent trustworthy meter reading. Only CLEAN measurements
 * qualify — a contaminated one is recorded on the run but is not something to
 * present as "this zone's flow".
 */
function latestCleanFlow(runs: RunRecord[], zoneId: string): { gpm: number; at: string } | undefined {
  const measured = runs
    .filter((r) => r.zoneId === zoneId && r.measuredConfidence === 'CLEAN' && r.measuredGalPerMin !== undefined)
    .sort((a, b) => (a.actualEnd ?? a.scheduledAt).localeCompare(b.actualEnd ?? b.scheduledAt));
  const last = measured.at(-1);
  return last ? { gpm: last.measuredGalPerMin!, at: last.actualEnd ?? last.scheduledAt } : undefined;
}

/**
 * "3 × 2 min at 9:00 AM, 12:00 PM, 3:00 PM": how a misting program reads to a
 * homeowner. Shared by the card and the editor preview so they can't drift.
 */
function describeNewSeed(program: NewSeedProgram): string {
  return `${program.times.length} × ${program.runMinutes} min at ${program.times.map(fmtTime).join(', ')}`;
}

function ZoneRow({
  zone,
  flow,
  sensor,
  targetName,
  onEdit,
  onDelete,
  onRun,
  onReview,
  onCalibrate,
  onFlowReview,
  onCheckCalibration,
  onStartCalibration,
  onDismissSession,
  onDismissPrecip,
  onDismissDisagreement,
  onHistory,
  onToggle,
}: {
  zone: ZoneWithSoil;
  flow: { gpm: number; at: string } | undefined;
  sensor: SensorSummary | undefined;
  targetName: string | undefined;
  onEdit: () => void;
  onDelete: () => void;
  onRun: () => void;
  onReview: () => void;
  onCalibrate: () => void;
  onFlowReview: () => void;
  onCheckCalibration: () => void;
  onStartCalibration: () => void;
  onDismissSession: () => void;
  onDismissPrecip: () => void;
  onDismissDisagreement: () => void;
  onHistory: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const est = sessionEstimate(zone);
  const session = zone.calibrationSession;
  const hint = zone.precipRateHint;
  const disagreement = zone.sensorDisagreementAlert;
  // A new-seed program is bounded, so "has one" and "is running one" are
  // different things: an upcoming program is worth showing, but it doesn't yet
  // displace the zone's normal schedule.
  const today = toIsoDate(new Date());
  const seed = zone.newSeed;
  const seedRunning = seed !== undefined && newSeedActive(zone, today);
  const seedUpcoming = seed !== undefined && !seedRunning && today < seed.startDate;

  /*
    Secondary actions, in priority order: things the zone is actively asking
    about first, then routine edits, then the destructive one last and set
    apart.

    The three calibration entries used to sit side by side on the card as
    "Review calibration", "Check calibration" and "Calibrate sensor" — three
    buttons sharing a root word, one of which silently opens a valve. They are
    named for what they do now, so an inspection can't be mistaken for a run.
  */
  const menuItems: MenuItem[] = [
    ...(zone.drift ? [{ label: 'Review Rachio changes', onSelect: onReview, emphasis: true }] : []),
    ...(zone.calibrationSuggestion
      ? [{ label: 'Review calibration suggestion', onSelect: onCalibrate, emphasis: true }]
      : []),
    ...(zone.flowAnomaly ? [{ label: 'Review water flow', onSelect: onFlowReview, emphasis: true }] : []),
    { label: 'Edit zone', onSelect: onEdit },
    ...(zone.moistureSensorId && zone.moistureHighPct !== undefined
      ? [{ label: 'Check calibration status', onSelect: onCheckCalibration }]
      : []),
    ...(zone.moistureSensorId && session?.status !== 'WAITING'
      ? [{ label: 'Run calibration soak (waters now)', onSelect: onStartCalibration }]
      : []),
    { label: 'Delete zone', onSelect: onDelete, danger: true },
  ];

  return (
    // `tabIndex` is what lets an attention link put focus on this card; it
    // stays out of the tab order itself.
    <div className={`zone-card${zone.enabled ? '' : ' off'}`} role="listitem" id={rowDomId('zones', zone.id)} tabIndex={-1}>
      <div className="zone-num" aria-hidden="true">
        {zone.zoneNumber}
      </div>
      <div className="grow">
        <div className="name">
          {zone.name}
          {!zone.enabled && (
            <Pill tone="muted" plain>
              Paused
            </Pill>
          )}
          {zone.drift && <Pill tone="warn">Changed in Rachio</Pill>}
          {zone.calibrationSuggestion && <Pill tone="accent">Calibration suggestion</Pill>}
          {session?.status === 'WAITING' && <Pill tone="accent">Calibrating…</Pill>}
          {zone.sensorResponseAlert && <Pill tone="warn">No moisture rise</Pill>}
          {zone.sensorDisagreementAlert && <Pill tone="warn">Sensor disagrees with model</Pill>}
          {zone.flowAnomaly?.kind === 'NO_FLOW' && <Pill tone="bad">No water flow</Pill>}
          {zone.flowAnomaly?.kind === 'HIGH_FLOW' && <Pill tone="warn">Using more water</Pill>}
          {zone.flowAnomaly?.kind === 'LOW_FLOW' && <Pill tone="warn">Using less water</Pill>}
          {seedRunning && (
            <Pill tone="accent">
              New seed: {seed!.times.length}×{seed!.runMinutes} min until {shortDate(seed!.endDate)}
            </Pill>
          )}
          {seedUpcoming && (
            <Pill tone="muted" plain>
              New seed from {shortDate(seed!.startDate)}
            </Pill>
          )}
        </div>
        <div className="zone-tags">
          <span className="tag">{PLANT_LABELS[zone.plantType]}</span>
          <span className="tag">{SOIL_LABELS[zone.soilType]}</span>
          <span className="tag">{NOZZLE_LABELS[zone.nozzleType]}</span>
          <span className="tag">{SUN_LABELS[zone.sunExposure]}</span>
          {zone.slope !== 'FLAT' && <span className="tag">{SLOPE_LABELS[zone.slope]}</span>}
          {zone.scheduleMode === 'FIXED' && <span className="tag">Fixed schedule</span>}
          {zone.mirrorZoneId && <span className="tag">Waters with {targetName ?? 'another zone'}</span>}
          {sensor?.lastReading && (
            <span className="tag">Soil {fmtPct(sensor.lastReading.moisturePct)} · {relativeTime(sensor.lastReading.at)}</span>
          )}
          {(flow || zone.flowBaseline) && (
            <span className="tag">
              {flow
                ? `Flow ${flow.gpm.toFixed(1)} gpm` +
                  (zone.flowBaseline ? ` · baseline ${zone.flowBaseline.galPerMin.toFixed(1)}` : '') +
                  ` · ${relativeTime(flow.at)}`
                : `Flow baseline ${zone.flowBaseline!.galPerMin.toFixed(1)} gpm`}
            </span>
          )}
        </div>
        {seedRunning ? (
          <div className="estimate">
            New seed misting: {describeNewSeed(seed!)} ≈{' '}
            {fmtInches(newSeedDailyDepthIn(seed!, zone))} a day, through {shortDate(seed!.endDate)}. The zone's normal
            schedule resumes on its own after that.
          </div>
        ) : zone.mirrorZoneId ? (
          <div className="estimate">
            {zone.fixedRuntimeMinutes ? (
              <>
                Waters {fmtMinutes(zone.fixedRuntimeMinutes)} whenever {targetName ?? 'its target zone'} waters (fixed
                drip duration).
              </>
            ) : (
              <>
                Waters whenever {targetName ?? 'its target zone'} waters — same amount, sized to this zone's own nozzle
                and soil.
              </>
            )}
          </div>
        ) : zone.scheduleMode === 'FIXED' && zone.fixedSchedule ? (
          <div className="estimate">
            Fixed schedule — {describeDays(zone.fixedSchedule.days)} · {fmtMinutes(zone.fixedSchedule.runMinutes)} ≈{' '}
            {fmtInches((zone.fixedSchedule.runMinutes / 60) * zone.precipRateInPerHr)} per run
            {zone.fixedSchedule.smartSkips ? ' · smart skips on' : ' · runs in any weather'}
          </div>
        ) : zone.fixedRuntimeMinutes ? (
          <div className="estimate">
            Waters {fmtMinutes(zone.fixedRuntimeMinutes)} when the soil model says it's due (fixed drip duration).
          </div>
        ) : (
          est && (
            <div className="estimate">
              Typical session ≈ {fmtInches(est.grossIn)} over {fmtMinutes(est.minutes)} at {zone.precipRateInPerHr}{' '}
              in/hr
            </div>
          )
        )}
        {zone.enabled && zone.soil && <SoilMeter zone={zone} targetName={targetName} />}
        {zone.sensorResponseAlert && (
          <Banner tone="info">
            Last run barely moved this zone's sensor (
            {zone.sensorResponseAlert.baselinePct}% → {zone.sensorResponseAlert.postPct}%). The runtime may be too
            short, or water isn't reaching the zone — check the schedule, valve, and sprinkler.
          </Banner>
        )}
        {disagreement && (
          <Banner
            tone="info"
            action={
              <Button variant="ghost" size="sm" onClick={onDismissDisagreement}>
                Dismiss
              </Button>
            }
          >
            For {disagreement.days} days the weather model has said this zone needs water (
            {disagreement.modeledDeficitIn.toFixed(2)} in short) while its sensor has read{' '}
            {disagreement.sensorPct}% ({disagreement.sensorDeficitIn.toFixed(2)} in short). Either the probe isn't
            seeing the root zone (too deep, a damp spot, 2 in is right for turf) or its "full" / "water at" bounds
            are set too low. If the grass looks fine, dismiss this.
          </Banner>
        )}
        {zone.flowAnomaly && (
          <Banner
            tone={zone.flowAnomaly.kind === 'NO_FLOW' ? 'error' : 'info'}
            action={
              <Button variant="ghost" size="sm" onClick={onFlowReview}>
                Review
              </Button>
            }
          >
            {zone.flowAnomaly.kind === 'NO_FLOW' ? (
              <>
                This zone ran but the water meter measured no flow. Check its valve, shutoff, and wiring — until
                it is fixed the soil model believes the zone was watered when it was not.
              </>
            ) : (
              <>
                Measured {zone.flowAnomaly.observedGalPerMin} gpm over {zone.flowAnomaly.runCount} runs, against{' '}
                {zone.flowAnomaly.baselineGalPerMin} gpm normally.{' '}
                {zone.flowAnomaly.kind === 'HIGH_FLOW'
                  ? 'Often a broken head or a leaking line.'
                  : 'Often a clogged nozzle or a partly closed valve.'}
              </>
            )}
          </Banner>
        )}
        {session?.status === 'INCONCLUSIVE' && (
          <Banner
            tone="info"
            action={
              <Button variant="ghost" size="sm" onClick={onDismissSession}>
                Dismiss
              </Button>
            }
          >
            Calibration run for {zone.name} was inconclusive. {CALIBRATION_FAILURE_COPY[session.failure ?? 'NO_DATA']}
          </Banner>
        )}
        {session?.status === 'DONE' && !zone.calibrationSuggestion && (
          <Banner
            tone="ok"
            action={
              <Button variant="ghost" size="sm" onClick={onDismissSession}>
                Dismiss
              </Button>
            }
          >
            Calibration run settled around <b>{session.observedHighPct}%</b> — this zone's "Full" ({zone.moistureHighPct}
            %) already looks right, so nothing to change.
          </Banner>
        )}
        {hint && (
          <Banner
            tone="info"
            action={
              <Button variant="ghost" size="sm" onClick={onDismissPrecip}>
                Dismiss
              </Button>
            }
          >
            Over {hint.runCount} runs, the sensor shows about <b>{fmtInches(hint.impliedNetIn)}</b> of water reaching the
            root zone per run, but this zone is configured for <b>{fmtInches(hint.configuredNetIn)}</b>.{' '}
            {precipHintAction(hint, zone)}
          </Banner>
        )}
      </div>
      <div className="zone-actions">
        <Switch checked={zone.enabled} onChange={onToggle} label={`Enable ${zone.name}`} />
        {/*
          Two common actions stay visible; everything else moves into the
          overflow. Previously all six-to-eight sat here at equal weight and
          wrapped differently on every card, so "Run now" — the action you
          actually want standing at the valve box — was never in the same place
          twice, and Delete landed in the easiest thumb position.
        */}
        <Button variant="subtle" size="sm" onClick={onRun}>
          Run now
        </Button>
        <Button variant="ghost" size="sm" onClick={onHistory}>
          History
        </Button>
        <Menu label={`More actions for ${zone.name}`} items={menuItems} />
      </div>
    </div>
  );
}

/** Soil-bucket usage plus the forecast-projected next watering day. */
function SoilMeter({ zone, targetName }: { zone: ZoneWithSoil; targetName?: string }) {
  const soil = zone.soil!;
  const triggerIn = allowedDepletionIn(zone);
  if (triggerIn <= 0) return null;
  const usedPct = Math.min(100, Math.round((soil.deficitIn / triggerIn) * 100));

  const today = toIsoDate(new Date());
  const next = soil.projectedNextWateringDate;
  const label =
    next === undefined
      ? 'Next: 10+ days away'
      : next < today
        ? 'Next: recalculating after watering'
        : `Next: ${friendlyDate(next)}`;
  const lastWatered = soil.lastWateredAt;
  const lastLabel = lastWatered ? `Last: ${friendlyDate(lastWatered.slice(0, 10))}` : 'Last: no record';

  // A mirror waters when its target does, so its own bucket is not the trigger.
  // Showing the bar implied otherwise — "100% used" next to a date nine days out.
  if (zone.mirrorZoneId) {
    return (
      <div className="soil-meter">
        <span>
          Follows {targetName ?? 'its target zone'} · {label} ·{' '}
          <span title={lastWatered ? clockTime(lastWatered) : undefined}>{lastLabel}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="soil-meter" title={`Soil budget used: ${usedPct}% of the ${triggerIn.toFixed(2)} in trigger`}>
      <div className="meter" role="presentation">
        <div className={`meter-fill${usedPct >= 80 ? ' high' : ''}`} style={{ width: `${usedPct}%` }} />
      </div>
      <span>
        Soil budget {usedPct}% used · {label} ·{' '}
        <span title={lastWatered ? clockTime(lastWatered) : undefined}>{lastLabel}</span>
      </span>
    </div>
  );
}

function DeleteConfirm({
  zone,
  onClose,
  onDeleted,
}: {
  zone: Zone;
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const doDelete = async () => {
    setBusy(true);
    setError('');
    try {
      await api.deleteZone(zone.id);
      await onDeleted();
    } catch (e) {
      setError(errorMessage(e, 'Failed to delete.'));
      setBusy(false);
    }
  };

  return (
    <Modal narrow title={`Delete "${zone.name}"?`} onClose={onClose}>
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        <p className="muted small">
          This removes the zone and its settings from the scheduler. The zone still exists on your Rachio controller
          and can be re-imported later.
        </p>
        <div className="actions">
          <Button variant="danger" busy={busy} onClick={() => void doDelete()}>
            Delete zone
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------- drift review ---------- */

function driftValue(field: ZoneDriftField['field'], value: string | number): string {
  switch (field) {
    case 'plantType':
      return PLANT_LABELS[value as keyof typeof PLANT_LABELS] ?? String(value);
    case 'nozzleType':
      return NOZZLE_LABELS[value as keyof typeof NOZZLE_LABELS] ?? String(value);
    case 'soilType':
      return SOIL_LABELS[value as keyof typeof SOIL_LABELS] ?? String(value);
    case 'slope':
      return SLOPE_LABELS[value as keyof typeof SLOPE_LABELS] ?? String(value);
    case 'sunExposure':
      return SUN_LABELS[value as keyof typeof SUN_LABELS] ?? String(value);
    case 'precipRateInPerHr':
      return `${value} in/hr`;
    case 'rootDepthIn':
      return `${value} in`;
    case 'areaSqFt':
      return `${value} sq ft`;
    default:
      return String(value);
  }
}

/** Review settings that changed on the Rachio controller since import. */
function DriftReview({
  zone,
  onClose,
  onResolved,
}: {
  zone: Zone;
  onClose: () => void;
  onResolved: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'apply' | 'dismiss' | ''>('');
  const [error, setError] = useState('');
  const drift = zone.drift;
  if (!drift) return null;

  const act = async (which: 'apply' | 'dismiss') => {
    setBusy(which);
    setError('');
    try {
      if (which === 'apply') {
        await api.applyZoneDrift(zone.id);
        onResolved(`Updated "${zone.name}" to match Rachio.`);
      } else {
        await api.dismissZoneDrift(zone.id);
        onResolved(`Keeping your settings for "${zone.name}".`);
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not resolve the change.'));
      setBusy('');
    }
  };

  return (
    <Modal
      title={`"${zone.name}" changed in Rachio`}
      sub={`Detected ${new Date(drift.detectedAt).toLocaleDateString()} — the controller's settings differ from this scheduler's.`}
      onClose={onClose}
    >
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        <div className="list">
          {drift.fields.map((f) => (
            <div className="run-row" key={f.field}>
              <div className="grow">
                <div className="title">{DRIFT_FIELD_LABELS[f.field]}</div>
                <div className="meta">
                  Yours: {driftValue(f.field, f.ours)} → Rachio: <b>{driftValue(f.field, f.theirs)}</b>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="muted small">
          Applying updates this scheduler's zone settings (and future watering math) to match the controller.
          Keeping yours leaves everything as-is and won't re-flag unless Rachio's values change again.
        </p>

        <div className="actions">
          <Button busy={busy === 'apply'} disabled={busy === 'dismiss'} onClick={() => void act('apply')}>
            Use Rachio's settings
          </Button>
          <Button variant="ghost" busy={busy === 'dismiss'} disabled={busy === 'apply'} onClick={() => void act('dismiss')}>
            Keep mine
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Review what a water meter measured against the zone's learned normal.
 *
 * The two buttons mean genuinely different things, so the copy has to be clear
 * about which is which: accepting re-learns the baseline (right when the change
 * was deliberate — new nozzles, a repaired line), while dismissing keeps the
 * old baseline and only silences this reading. Accepting a reading caused by an
 * actual leak would teach the system that the leak is normal.
 */
function FlowReview({
  zone,
  onClose,
  onResolved,
}: {
  zone: Zone;
  onClose: () => void;
  onResolved: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'apply' | 'dismiss' | ''>('');
  const [error, setError] = useState('');
  const anomaly = zone.flowAnomaly;
  if (!anomaly) return null;

  const act = async (which: 'apply' | 'dismiss') => {
    setBusy(which);
    setError('');
    try {
      if (which === 'apply') {
        await api.applyFlowAnomaly(zone.id);
        onResolved(`"${zone.name}" re-baselined to ${anomaly.observedGalPerMin} gpm.`);
      } else {
        await api.dismissFlowAnomaly(zone.id);
        onResolved(`Dismissed the flow alert for "${zone.name}".`);
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not resolve the alert.'));
      setBusy('');
    }
  };

  const noFlow = anomaly.kind === 'NO_FLOW';
  const high = anomaly.kind === 'HIGH_FLOW';

  return (
    <Modal
      title={
        noFlow
          ? `"${zone.name}" ran but no water moved`
          : `"${zone.name}" is using ${high ? 'more' : 'less'} water than usual`
      }
      sub={`Measured ${new Date(anomaly.at).toLocaleDateString()} across ${anomaly.runCount} run${anomaly.runCount === 1 ? '' : 's'}.`}
      onClose={onClose}
    >
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        <div className="list">
          <div className="run-row">
            <div className="grow">
              <div className="title">Measured flow</div>
              <div className="meta">
                {anomaly.observedGalPerMin} gpm
                {anomaly.baselineGalPerMin !== undefined && (
                  <>
                    {' '}
                    · normally <b>{anomaly.baselineGalPerMin} gpm</b>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {noFlow ? (
          <p className="muted small">
            The controller reported this zone as watered, but the meter measured essentially no water. Check the
            zone valve, its shutoff, and the solenoid wiring. Until this is fixed the soil model believes the zone
            was watered when it was not, so it will keep under-watering it.
          </p>
        ) : (
          <p className="muted small">
            {high
              ? 'A sustained jump usually means a broken head, a cracked lateral, or a blown seal.'
              : 'A sustained drop usually means a clogged nozzle, a partly closed valve, or lower supply pressure.'}{' '}
            If you changed something here on purpose, accept the new normal so future runs are judged against it.
            Dismissing keeps the old normal and only silences this reading.
          </p>
        )}

        <div className="actions">
          {!noFlow && (
            <Button busy={busy === 'apply'} disabled={busy === 'dismiss'} onClick={() => void act('apply')}>
              This is the new normal
            </Button>
          )}
          <Button
            variant={noFlow ? 'primary' : 'ghost'}
            busy={busy === 'dismiss'}
            disabled={busy === 'apply'}
            onClick={() => void act('dismiss')}
          >
            Dismiss
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Review a data-driven suggestion to recalibrate the zone's sensor bounds. */
function CalibrationReview({
  zone,
  onClose,
  onResolved,
}: {
  zone: Zone;
  onClose: () => void;
  onResolved: (message: string) => void;
}) {
  const [busy, setBusy] = useState<'apply' | 'dismiss' | ''>('');
  const [error, setError] = useState('');
  const s = zone.calibrationSuggestion;
  if (!s) return null;

  const act = async (which: 'apply' | 'dismiss') => {
    setBusy(which);
    setError('');
    try {
      if (which === 'apply') {
        await api.applyCalibration(zone.id);
        onResolved(`Updated "${zone.name}" sensor bounds.`);
      } else {
        await api.dismissCalibration(zone.id);
        onResolved(`Keeping your bounds for "${zone.name}".`);
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not resolve the suggestion.'));
      setBusy('');
    }
  };

  // The "Full" observation comes from one of three sources; word the subtitle
  // to match how it was derived.
  const basis =
    s.method === 'EVENTS'
      ? `Based on ${s.eventCount ?? 0} watering/rain event${s.eventCount === 1 ? '' : 's'}.`
      : s.method === 'CALIBRATION_RUN'
        ? 'From your calibration run.'
        : `Based on ${s.readingCount.toLocaleString()} readings over ${s.days} days.`;

  return (
    <Modal title={`Recalibrate "${zone.name}"?`} sub={basis} onClose={onClose}>
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        <p className="small">
          After watering, your sensor settles around <b>{s.observedHighPct}%</b>, but this zone's "Full" is set to{' '}
          <b>{zone.moistureHighPct}%</b>. Suggested bounds — Full: <b>{s.suggestedHighPct}%</b> (now{' '}
          {zone.moistureHighPct}%), Water at: <b>{s.suggestedLowPct}%</b> (now {zone.moistureLowPct}%).
        </p>
        <p className="muted small">
          Applying rescales how readings convert to soil deficit; it doesn't water anything now. Keeping yours won't
          re-flag unless the observed value changes again.
        </p>
        <div className="actions">
          <Button busy={busy === 'apply'} disabled={busy === 'dismiss'} onClick={() => void act('apply')}>
            Use suggested bounds
          </Button>
          <Button variant="ghost" busy={busy === 'dismiss'} disabled={busy === 'apply'} onClick={() => void act('dismiss')}>
            Keep mine
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * What to tell the user when the check declines to suggest anything.
 *
 * "Not enough data yet" is the wrong answer for most of these: the remedies are
 * different. A soil that holds water for days will never produce settled
 * routine readings no matter how long you wait, so waiting is bad advice and a
 * calibration run is the way out. Disagreeing evidence does resolve on its own
 * with the next soak. NOT_ENOUGH_DATA genuinely is "come back later", so it
 * keeps the generic copy below.
 */
const CALIBRATION_HOLD_COPY: Partial<Record<CalibrationHoldReason, string>> = {
  UNSETTLED_DRAINAGE:
    'This soil holds water for days after rain, so routine readings never settle enough to calibrate from. Run a calibration cycle instead: it waters once, then watches for the settled value directly.',
  EVENTS_CONTRADICT:
    'The settled readings after recent soaks disagree with the overall pattern, so nothing is suggested yet. More watering or rain events will resolve it, or run a calibration cycle.',
  NO_SATURATION_EVIDENCE:
    'Readings sit below this zone\'s "Full", but nothing has fully soaked the soil recently, so lowering it could hide under-watering. Check again after a good soak or a rainstorm.',
};

/**
 * On-demand calibration check: computes where the sensor's bounds stand right
 * now (no waiting for the daily pass), shows the moisture graph with the
 * detected field-capacity plateaus and any suggested bounds overlaid, and — when
 * there's a suggestion — offers to apply it inline.
 */
function CalibrationCheck({
  zone,
  onClose,
  onResolved,
}: {
  zone: ZoneWithSoil;
  onClose: () => void;
  onResolved: (message: string) => void;
}) {
  const load = useCallback(
    () =>
      Promise.all([
        api.zoneHistory(zone.id, 30),
        api.listRuns(recentDates(30)),
        api.checkCalibration(zone.id),
      ]).then(([history, runs, check]) => ({
        ...history,
        runs: runs.filter((r) => r.zoneId === zone.id),
        check,
      })),
    [zone.id],
  );
  const { data, loading, error } = useLoad(load);
  const [busy, setBusy] = useState<'apply' | 'dismiss' | ''>('');
  const [actErr, setActErr] = useState('');

  const act = async (which: 'apply' | 'dismiss') => {
    setBusy(which);
    setActErr('');
    try {
      if (which === 'apply') {
        await api.applyCalibration(zone.id);
        onResolved(`Updated "${zone.name}" sensor bounds.`);
      } else {
        await api.dismissCalibration(zone.id);
        onResolved(`Keeping your bounds for "${zone.name}".`);
      }
    } catch (e) {
      setActErr(errorMessage(e, 'Could not resolve the suggestion.'));
      setBusy('');
    }
  };

  const preview = data?.check.preview;
  const suggestion = preview?.status === 'SUGGESTION' ? preview.suggestion : undefined;
  const basis =
    preview?.method === 'EVENTS'
      ? `Based on ${preview.eventCount ?? 0} watering/rain event${preview.eventCount === 1 ? '' : 's'}.`
      : preview?.method === 'PERCENTILE'
        ? `Based on ${preview.readingCount.toLocaleString()} readings over ${preview.days} days.`
        : undefined;

  return (
    <Modal title={`Calibration check — ${zone.name}`} sub={basis} onClose={onClose}>
      {loading ? (
        <Skeleton height="240px" />
      ) : error || !data || !preview ? (
        <Banner tone="error">{error || 'Failed to check calibration.'}</Banner>
      ) : (
        <div className="stack">
          <SoilHistoryChart
            zone={zone}
            snapshots={data.snapshots}
            readings={data.readings}
            runs={data.runs}
            defaultMode="moisture"
            suggestedLowPct={suggestion?.suggestedLowPct}
            suggestedHighPct={suggestion?.suggestedHighPct}
          />

          {actErr && <Banner tone="error">{actErr}</Banner>}

          {preview.status === 'SUGGESTION' && suggestion ? (
            <>
              <p className="small">
                After watering, your sensor settles around <b>{preview.observedHighPct}%</b>, but this zone's "Full" is
                set to <b>{zone.moistureHighPct}%</b>. Suggested bounds — Full: <b>{suggestion.suggestedHighPct}%</b> (now{' '}
                {zone.moistureHighPct}%), Water at: <b>{suggestion.suggestedLowPct}%</b> (now {zone.moistureLowPct}%).
              </p>
              <p className="muted small">
                Applying rescales how readings convert to soil deficit; it doesn't water anything now.
              </p>
              <div className="actions">
                <Button busy={busy === 'apply'} disabled={busy === 'dismiss'} onClick={() => void act('apply')}>
                  Use suggested bounds
                </Button>
                <Button variant="ghost" busy={busy === 'dismiss'} disabled={busy === 'apply'} onClick={() => void act('dismiss')}>
                  Keep mine
                </Button>
              </div>
            </>
          ) : preview.status === 'WITHIN_TOLERANCE' ? (
            <Banner tone="ok">
              Your sensor settles around <b>{preview.observedHighPct}%</b> after watering — this zone's "Full" (
              {zone.moistureHighPct}%) already looks right, so there's nothing to change.
            </Banner>
          ) : (
            <Banner tone="info">
              {(preview.reason && CALIBRATION_HOLD_COPY[preview.reason]) ??
                'Not enough watering or rain events yet to judge this sensor. Check again after the next good soak, or a rainstorm.'}
            </Banner>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Manual run: pick any number of zones, one duration, one click.
 *
 * One modal rather than one per zone, because the controller can only honor one
 * command: each single-zone start is last-command-wins on Rachio, so starting
 * four zones from four dialogs silently aborted the first three. The batch goes
 * out as a single request and Rachio runs the zones back to back.
 *
 * Disabled zones are still offered. "Enabled" governs the SCHEDULE; a manual run
 * is the user standing at the valve box asking for water now, which is exactly
 * when you want to test a zone you have paused. The chip says it is off so the
 * choice is deliberate.
 */
function RunZones({
  zones,
  initial,
  onClose,
  onStarted,
}: {
  zones: Zone[];
  initial?: Zone;
  onClose: () => void;
  onStarted: (message: string) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(initial ? [initial.id] : []));
  const [minutes, setMinutes] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const chosen = zones.filter((z) => selected.has(z.id));
  const parsedMinutes = Number(minutes.trim());
  // 180 mirrors the backend's RACHIO_MAX_RUN_MINUTES (packages/backend/src/
  // rachio/client.ts), which the web package cannot import: @irrigation/core is
  // the only shared dependency and it knows nothing about the controller. The
  // server enforces the real bound, including a tenant cap tighter than this.
  const validMinutes = Number.isFinite(parsedMinutes) && parsedMinutes >= 1 && parsedMinutes <= 180;

  const durationHint =
    chosen.length === 0
      ? 'Pick at least one zone.'
      : !validMinutes
        ? 'Enter a duration between 1 and 180 minutes.'
        : batchSummary(chosen.length, parsedMinutes);

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const start = async () => {
    if (!validMinutes) {
      setError('Enter a duration between 1 and 180 minutes.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // Sorted by zone number, which is the order they run in: the API takes
      // request order as the run order.
      await api.runZones(chosen.map((z) => ({ zoneId: z.id, minutes: parsedMinutes })));
      onStarted(batchToast(chosen.map((z) => z.name), parsedMinutes));
    } catch (e) {
      // The server owns the real runtime cap and the busy-controller refusal,
      // so show what it said rather than guessing at it.
      setError(errorMessage(e, 'Could not start the zones.'));
      setBusy(false);
    }
  };

  return (
    <Modal
      narrow
      title="Run zones now"
      sub="Starts your sprinklers immediately: this opens the valve for real. Zones run one after another, not together."
      onClose={onClose}
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy && chosen.length > 0) void start();
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        {/* No autoFocus: Modal takes initial focus itself so the dialog is
            announced, and an autoFocus here would only race it. */}
        <div className="field">
          {/* Labels a GROUP, not a single control, so it gets an id the group
              points at rather than an htmlFor that has nothing to name. */}
          <label id="run-zones-label">Zones</label>
          <div className="chip-row" role="group" aria-labelledby="run-zones-label">
            {zones.map((z) => (
              <button
                key={z.id}
                type="button"
                className={`chip${selected.has(z.id) ? ' on' : ''}`}
                aria-pressed={selected.has(z.id)}
                onClick={() => toggle(z.id)}
              >
                {z.zoneNumber}. {z.name}
                {!z.enabled && ' (off)'}
              </button>
            ))}
          </div>
          <div className="actions">
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set(zones.map((z) => z.id)))}>
              All
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              None
            </Button>
          </div>
        </div>
        {/* The batch summary rides in as the field's hint rather than a loose
            span: `.hint` is only styled inside `.field`, and this way it is
            wired to the input through aria-describedby. */}
        <Field label="Duration per zone (minutes)" hint={durationHint}>
          {(id) => (
            <TextInput
              id={id}
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          )}
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy} disabled={chosen.length === 0}>
            Start watering
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Confirm and start a calibration run: water briefly, evaluate later. */
function StartCalibration({
  zone,
  config,
  onClose,
  onStarted,
}: {
  zone: Zone;
  config: ScheduleConfig;
  onClose: () => void;
  onStarted: () => void;
}) {
  const recommended = calibrationRunMinutes(zone, config);
  const cap = Math.max(1, Math.floor(calibrationRuntimeCap(zone, config)));
  const [minutes, setMinutes] = useState(String(recommended));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const drainHours = DRAINAGE_HOURS[zone.soilType];

  const start = async () => {
    const m = Number(minutes.trim());
    if (!Number.isFinite(m) || m < 1 || m > cap) {
      setError(`Enter a run time between 1 and ${cap} minutes.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.calibrateZone(zone.id, m);
      onStarted();
    } catch (e) {
      setError(errorMessage(e, 'Could not start the calibration run.'));
      setBusy(false);
    }
  };

  return (
    <Modal
      narrow
      title={`Calibrate "${zone.name}"`}
      sub="Waters briefly now to find this zone's field capacity."
      onClose={onClose}
    >
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        <p className="small">
          This runs the zone right now — a full root-zone refill, enough to push the soil past field capacity so it can
          drain back to a readable plateau. The recommended run is about <b>{fmtMinutes(recommended)}</b>; override it
          below if you need to. The usual safety caps still apply, and this opens the valve for real.
        </p>
        <Field
          label="Run time (minutes)"
          hint={`Recommended ${recommended} min · capped at ${cap} min by your safety limits.`}
        >
          {(id) => (
            <TextInput
              id={id}
              inputMode="numeric"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          )}
        </Field>
        <p className="muted small">
          Afterward the soil drains toward field capacity over the next several hours to a day (about {drainHours} h for{' '}
          {SOIL_LABELS[zone.soilType].toLowerCase()}). The scheduler watches the sensor and, once it settles, either
          suggests new "Full"/"Water at" bounds or confirms yours look right — you don't need to do anything else.
        </p>
        <div className="actions">
          <Button busy={busy} onClick={() => void start()}>
            Water &amp; calibrate
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------- editor ---------- */

/**
 * Starting points for a misting schedule. Two or three light passes across the
 * warm part of the day is what keeps a seedbed damp without drowning it; the
 * user can edit any of them afterwards.
 */
const SEED_TIME_PRESETS: { label: string; times: string[] }[] = [
  { label: '2× a day', times: ['10:00', '14:00'] },
  { label: '3× a day', times: ['09:00', '12:00', '15:00'] },
  { label: '4× a day', times: ['09:00', '11:30', '14:00', '16:30'] },
];

/** Minimum spacing between misting cycles, minutes. Must match the server. */
const SEED_MIN_SPACING_MINUTES = 30;
/** Longest a program may run, days. Must match the server. */
const SEED_MAX_SPAN_DAYS = 60;

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const seedMinuteOf = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
const fmtHhmm = (minuteOfDay: number): string => {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minuteOfDay)));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/**
 * Validate a misting program exactly as the server does, so the user is told
 * what is wrong before the round trip. Returns the error message, or undefined
 * when the draft is good.
 */
function validateSeedDraft(program: NewSeedProgram, zone: Zone, config: ScheduleConfig): string | undefined {
  if (program.times.length < 1 || program.times.length > 6) return 'Pick between 1 and 6 misting times.';
  if (program.times.some((t) => !HHMM_RE.test(t))) return 'Every misting time must be a valid time of day.';
  for (let i = 1; i < program.times.length; i++) {
    const gap = seedMinuteOf(program.times[i]!) - seedMinuteOf(program.times[i - 1]!);
    if (gap <= 0) return 'Misting times must be different and in order through the day.';
    if (gap < SEED_MIN_SPACING_MINUTES) return `Leave at least ${SEED_MIN_SPACING_MINUTES} minutes between mistings.`;
  }
  if (!Number.isInteger(program.runMinutes) || program.runMinutes < 1 || program.runMinutes > 15) {
    return 'Each misting must be a whole number of minutes, 1 to 15.';
  }
  if (!program.startDate || !program.endDate) return 'Give the program a start and end date.';
  if (program.endDate < program.startDate) return 'The end date must be on or after the start date.';
  const spanDays =
    (Date.parse(`${program.endDate}T00:00:00Z`) - Date.parse(`${program.startDate}T00:00:00Z`)) / 86_400_000 + 1;
  if (!Number.isFinite(spanDays)) return 'Give the program a start and end date.';
  if (spanDays > SEED_MAX_SPAN_DAYS) return `A new-seed program can run for at most ${SEED_MAX_SPAN_DAYS} days.`;

  const dailyMinutes = newSeedDailyMinutes(program);
  if (config.maxZoneRuntimeMinutes > 0 && dailyMinutes > config.maxZoneRuntimeMinutes) {
    return `That is ${dailyMinutes} minutes a day, over this system's ${config.maxZoneRuntimeMinutes}-minute per-zone limit.`;
  }
  const dailyIn = newSeedDailyDepthIn(program, zone);
  if (config.maxDailyWaterIn > 0 && dailyIn > config.maxDailyWaterIn + 1e-9) {
    return `That applies ${fmtInches(dailyIn)} a day, over this system's ${config.maxDailyWaterIn} in daily limit for a zone.`;
  }
  return undefined;
}

/** Numeric fields are edited as strings so clearing/typing never coerces to 0. */
interface NumericDraft {
  precipRateInPerHr: string;
  rootDepthIn: string;
  allowedDepletionFraction: string;
  efficiency: string;
  areaSqFt: string;
}

interface NumericSpec {
  label: string;
  min: number;
  max: number;
  optional?: boolean;
}

const NUMERIC_SPECS: Record<keyof NumericDraft, NumericSpec> = {
  precipRateInPerHr: { label: 'Application rate', min: 0.05, max: 5 },
  rootDepthIn: { label: 'Root depth', min: 1, max: 48 },
  allowedDepletionFraction: { label: 'Allowed depletion', min: 0.1, max: 0.9 },
  efficiency: { label: 'Efficiency', min: 0.1, max: 1 },
  areaSqFt: { label: 'Area', min: 1, max: 100000, optional: true },
};

function parseNumeric(key: keyof NumericDraft, raw: string): { value?: number; error?: string } {
  const spec = NUMERIC_SPECS[key];
  const trimmed = raw.trim();
  if (trimmed === '') {
    return spec.optional ? {} : { error: `${spec.label} is required.` };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return { error: 'Enter a number.' };
  if (n < spec.min || n > spec.max) return { error: `Must be between ${spec.min} and ${spec.max}.` };
  return { value: n };
}

function ZoneEditor({
  zone,
  sensors,
  zones,
  config,
  onClose,
  onSaved,
}: {
  zone: Zone;
  sensors: SensorSummary[];
  zones: Zone[];
  /** Tenant safety caps, so the new-seed editor can check a program locally. */
  config: ScheduleConfig;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(zone.name);
  const [plantType, setPlantType] = useState(zone.plantType);
  const [soilType, setSoilType] = useState(zone.soilType);
  const [sunExposure, setSunExposure] = useState(zone.sunExposure);
  const [slope, setSlope] = useState(zone.slope);
  const [nozzleType, setNozzleType] = useState(zone.nozzleType);
  const [nums, setNums] = useState<NumericDraft>({
    precipRateInPerHr: String(zone.precipRateInPerHr),
    rootDepthIn: String(zone.rootDepthIn),
    allowedDepletionFraction: String(zone.allowedDepletionFraction),
    efficiency: String(zone.efficiency),
    areaSqFt: zone.areaSqFt !== undefined ? String(zone.areaSqFt) : '',
  });
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof NumericDraft, string>>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Scheduling mode + fixed program (kept when toggling back to Smart).
  const fs = zone.fixedSchedule;
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>(zone.scheduleMode ?? 'SMART');
  const [fixedKind, setFixedKind] = useState<'ANY' | 'SPECIFIC_DAYS' | 'INTERVAL'>(
    fs?.days.kind === 'SPECIFIC_DAYS' || fs?.days.kind === 'INTERVAL' ? fs.days.kind : 'ANY',
  );
  const [fixedWeekdays, setFixedWeekdays] = useState<Set<number>>(
    new Set(fs?.days.kind === 'SPECIFIC_DAYS' ? fs.days.weekdays : []),
  );
  const [fixedEveryN, setFixedEveryN] = useState(String(fs?.days.kind === 'INTERVAL' ? fs.days.everyNDays : 3));
  const [fixedMinutes, setFixedMinutes] = useState(String(fs?.runMinutes ?? 15));
  const [fixedSmartSkips, setFixedSmartSkips] = useState(fs?.smartSkips ?? false);

  // New seed: short misting runs at fixed local times for a bounded stretch.
  // An already-expired program is treated as off: the user is looking at a
  // zone that has already gone back to its normal schedule.
  const todayIso = toIsoDate(new Date());
  const ns = zone.newSeed;
  const [seedOn, setSeedOn] = useState(newSeedLive(ns, todayIso));
  const [seedTimes, setSeedTimes] = useState<string[]>(ns?.times ?? [...SEED_TIME_PRESETS[1]!.times]);
  const [seedMinutes, setSeedMinutes] = useState(String(ns?.runMinutes ?? 3));
  const [seedStart, setSeedStart] = useState(ns?.startDate ?? todayIso);
  const [seedEnd, setSeedEnd] = useState(ns?.endDate ?? isoDatePlus(todayIso, 21));

  // Watering-amount mode for SMART zones: AUTO computes runtime from the
  // deficit + precip rate; FIXED runs a user-set duration (for drip).
  const [amountMode, setAmountMode] = useState<'AUTO' | 'FIXED'>(zone.fixedRuntimeMinutes ? 'FIXED' : 'AUTO');
  const [fixedRuntime, setFixedRuntime] = useState(String(zone.fixedRuntimeMinutes ?? 20));

  // "Waters with another zone" (mirror). Mutually exclusive with a FIXED
  // schedule and with misting; eligible targets are SMART, non-mirror, not
  // currently-misted zones other than this one. Excluding the misted ones keeps
  // an unwinnable choice out of the dropdown, but the save path checks again:
  // this list is a snapshot that may have been fetched minutes ago.
  const [mirrorZoneId, setMirrorZoneId] = useState(zone.mirrorZoneId ?? 'NONE');
  const eligibleTargets = zones.filter(
    (z) =>
      z.id !== zone.id &&
      !z.mirrorZoneId &&
      (z.scheduleMode ?? 'SMART') === 'SMART' &&
      !newSeedLive(z.newSeed, todayIso),
  );
  const mirroring = mirrorZoneId !== 'NONE';
  // Resolved against every zone, not just the eligible ones, so the name still
  // reads back when a stored target has since dropped off the list.
  const mirrorTargetName = zones.find((z) => z.id === mirrorZoneId)?.name;

  // Zones that mirror THIS one. They follow its smart schedule, so anything
  // that takes this zone off that schedule (a new-seed program) would leave
  // them dry for the duration.
  const dependents = zones.filter((z) => z.id !== zone.id && z.mirrorZoneId === zone.id);
  const seedBlockedNote = seedOn ? undefined : mirrorDependentSeedBlock(dependents.map((z) => z.name));

  const [sensorId, setSensorId] = useState(zone.moistureSensorId ?? 'NONE');
  const [moistLow, setMoistLow] = useState(zone.moistureLowPct !== undefined ? String(zone.moistureLowPct) : '');
  const [moistHigh, setMoistHigh] = useState(zone.moistureHighPct !== undefined ? String(zone.moistureHighPct) : '');

  // Drip coverage: point-source drip wets only part of the canopy, so we model
  // it with a wetted fraction (smaller reservoir → short, frequent runs). A
  // coarse layout preset seeds the fraction + application rate; "Custom" lets
  // the user set the fraction directly. See drip-water-model spec.
  const isDripNozzle = nozzleType === 'DRIP' || nozzleType === 'BUBBLER';
  const [dripLayout, setDripLayout] = useState<DripLayout | 'CUSTOM'>(
    zone.dripParams?.layout ?? (zone.dripParams ? 'CUSTOM' : 'POINT_DENSE'),
  );
  const [wettedFraction, setWettedFraction] = useState(
    zone.dripParams?.wettedFraction !== undefined
      ? String(zone.dripParams.wettedFraction)
      : String(DRIP_LAYOUT_PRESETS.POINT_DENSE.wettedFraction),
  );

  /** Applying a preset sets both the wetted fraction and the application rate. */
  const applyDripLayout = (layout: DripLayout | 'CUSTOM') => {
    setDripLayout(layout);
    if (layout !== 'CUSTOM') {
      const preset = DRIP_LAYOUT_PRESETS[layout];
      setWettedFraction(String(preset.wettedFraction));
      setNum('precipRateInPerHr', String(preset.precipRateInPerHr));
    }
  };

  const setNum = (key: keyof NumericDraft, value: string) => {
    setNums((d) => ({ ...d, [key]: value }));
    setFieldErrors((e) => ({ ...e, [key]: undefined }));
  };

  const nozzleDefault = DEFAULT_PRECIP_RATE_IN_PER_HR[nozzleType];

  const save = async () => {
    if (!name.trim()) {
      setError('Give the zone a name.');
      return;
    }
    const parsed: Partial<Record<keyof NumericDraft, number>> = {};
    const errors: Partial<Record<keyof NumericDraft, string>> = {};
    for (const key of Object.keys(NUMERIC_SPECS) as (keyof NumericDraft)[]) {
      const res = parseNumeric(key, nums[key]);
      if (res.error) errors[key] = res.error;
      else if (res.value !== undefined) parsed[key] = res.value;
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Fix the highlighted fields.');
      return;
    }

    // A mirror follows its target's smart schedule, so it's never FIXED.
    const effectiveScheduleMode: ScheduleMode = mirroring ? 'SMART' : scheduleMode;

    // Fixed-schedule validation (only when that mode is selected).
    let fixedSchedule = zone.fixedSchedule;
    if (effectiveScheduleMode === 'FIXED') {
      const minutes = Number(fixedMinutes.trim());
      if (!Number.isFinite(minutes) || minutes < 1 || minutes > 360) {
        setError('Fixed run time must be between 1 and 360 minutes.');
        return;
      }
      let days: DayRestriction = { kind: 'ANY' };
      if (fixedKind === 'SPECIFIC_DAYS') {
        if (fixedWeekdays.size === 0) {
          setError('Pick at least one weekday for the fixed schedule.');
          return;
        }
        days = { kind: 'SPECIFIC_DAYS', weekdays: [...fixedWeekdays].sort() };
      } else if (fixedKind === 'INTERVAL') {
        const n = Number(fixedEveryN.trim());
        if (!Number.isInteger(n) || n < 1 || n > 31) {
          setError('Water every 1–31 days.');
          return;
        }
        days = { kind: 'INTERVAL', everyNDays: n };
      }
      fixedSchedule = { days, runMinutes: minutes, smartSkips: fixedSmartSkips };
    }

    // Fixed watering duration: for SMART zones (FIXED mode uses its own) and
    // for mirrors — a mirror can pin its own drip duration, running that long on
    // the target's watering days instead of matching the target's depth.
    const useFixedRuntime = effectiveScheduleMode === 'SMART' && amountMode === 'FIXED';
    let parsedFixedRuntime: number | undefined;
    if (useFixedRuntime) {
      parsedFixedRuntime = Number(fixedRuntime.trim());
      if (!Number.isFinite(parsedFixedRuntime) || parsedFixedRuntime < 1 || parsedFixedRuntime > 360) {
        setError('Watering duration must be between 1 and 360 minutes.');
        return;
      }
    }

    // Mirroring and a live new-seed program are mutually exclusive in both
    // directions, and the server answers 409. Check both here first: the
    // payload below sends `newSeed: null` while mirroring, so without this the
    // client would silently destroy a running program on its way to an error.
    if (mirroring) {
      const stored = zone.newSeed;
      if (stored && newSeedLive(stored, todayIso)) {
        setError(
          `This zone's new-seed program runs through ${shortDate(stored.endDate)}. Turn it off or wait for it to end before mirroring.`,
        );
        return;
      }
      // Belt and suspenders: such a target is already filtered out of the
      // dropdown, but `zones` is a snapshot and the program may be newer.
      const target = zones.find((z) => z.id === mirrorZoneId);
      if (target && newSeedLive(target.newSeed, todayIso)) {
        setError(
          `${target.name} is running a new-seed program and is misted on its own schedule, so it can't be mirrored until it ends.`,
        );
        return;
      }
    }

    // New-seed program. A mirror can't be misted (it follows its target's smart
    // schedule), so the section explains itself and sends no program while
    // mirroring. `null` clears a stored program, which is how a user cancels
    // one early; leaving an expired one stored would be harmless but confusing.
    let newSeed: NewSeedProgram | null = null;
    if (seedOn && !mirroring) {
      const draft: NewSeedProgram = {
        times: seedTimes.map((t) => t.trim()),
        runMinutes: Number(seedMinutes.trim()),
        startDate: seedStart,
        endDate: seedEnd,
      };
      const seedError = validateSeedDraft(draft, { ...zone, precipRateInPerHr: parsed.precipRateInPerHr! }, config);
      if (seedError) {
        setError(seedError);
        return;
      }
      newSeed = draft;
    }

    // Drip coverage validation (only for drip/bubbler nozzles).
    let dripParams: Zone['dripParams'] | null = null;
    if (isDripNozzle) {
      const wf = Number(wettedFraction.trim());
      if (!Number.isFinite(wf) || wf < 0.05 || wf > 1) {
        setError('Drip wetted fraction must be between 0.05 and 1.');
        return;
      }
      dripParams = { wettedFraction: wf, ...(dripLayout !== 'CUSTOM' ? { layout: dripLayout } : {}) };
    }

    // Moisture-sensor bounds validation (only when a sensor is selected).
    let parsedLow: number | undefined;
    let parsedHigh: number | undefined;
    if (sensorId !== 'NONE') {
      parsedLow = Number(moistLow.trim());
      parsedHigh = Number(moistHigh.trim());
      if (
        !Number.isFinite(parsedLow) ||
        !Number.isFinite(parsedHigh) ||
        parsedLow < 0 ||
        parsedLow > 100 ||
        parsedHigh < 0 ||
        parsedHigh > 100 ||
        parsedHigh <= parsedLow
      ) {
        setError('Moisture bounds must be 0–100 with Full above Water-at.');
        return;
      }
    }

    setBusy(true);
    setError('');
    try {
      // `soil` is a server-computed read model joined onto the zone for display.
      // Spreading it back would PUT a snapshot that was already stale when the
      // editor opened, over whatever the planner has since written.
      const { soil: _soil, ...persisted } = zone as ZoneWithSoil;
      await api.updateZone(zone.id, {
        ...persisted,
        name: name.trim(),
        scheduleMode: effectiveScheduleMode,
        mirrorZoneId: mirroring ? mirrorZoneId : '',
        fixedSchedule,
        plantType,
        soilType,
        sunExposure,
        slope,
        nozzleType,
        precipRateInPerHr: parsed.precipRateInPerHr!,
        rootDepthIn: parsed.rootDepthIn!,
        allowedDepletionFraction: parsed.allowedDepletionFraction!,
        efficiency: parsed.efficiency!,
        areaSqFt: parsed.areaSqFt,
        dripParams: dripParams ?? (null as unknown as undefined),
        fixedRuntimeMinutes: useFixedRuntime ? parsedFixedRuntime : (null as unknown as undefined),
        newSeed: newSeed ?? (null as unknown as undefined),
        moistureSensorId: sensorId === 'NONE' ? '' : sensorId,
        moistureLowPct: sensorId === 'NONE' ? undefined : parsedLow,
        moistureHighPct: sensorId === 'NONE' ? undefined : parsedHigh,
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Failed to save.'));
      setBusy(false);
    }
  };

  // Shown on the collapsed hardware section so its contents are knowable
  // without opening it.
  const hardwareSummary = `${NOZZLE_LABELS[nozzleType]} · ${nums.precipRateInPerHr || '—'} in/hr · root ${nums.rootDepthIn || '—'}"`;

  const previewZone: ZoneWithSoil = {
    ...zone,
    plantType,
    soilType,
    sunExposure,
    slope,
    nozzleType,
    precipRateInPerHr: Number(nums.precipRateInPerHr) || zone.precipRateInPerHr,
    rootDepthIn: Number(nums.rootDepthIn) || zone.rootDepthIn,
    allowedDepletionFraction: Number(nums.allowedDepletionFraction) || zone.allowedDepletionFraction,
    efficiency: Number(nums.efficiency) || zone.efficiency,
  };
  const preview = sessionEstimate(previewZone);
  const liveEstimate = preview
    ? `Typical session ≈ ${fmtInches(preview.grossIn)} over ${fmtMinutes(preview.minutes)}`
    : null;

  return (
    <Modal title={`Edit zone ${zone.zoneNumber}`} sub="These parameters drive the soil-moisture model." onClose={onClose}>
      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}

        <Section title="Plant & site">
          <Field label="Name">
            {(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>

          <div className="grid cols-2">
          <Field label="Plant type" hint="Sets the crop coefficient (how fast the zone dries out).">
            {(id) => <Select id={id} value={plantType} options={PLANT_TYPES} labels={PLANT_LABELS} onChange={setPlantType} />}
          </Field>
          <Field label="Soil type" hint="Sets water-holding capacity and soak behavior.">
            {(id) => <Select id={id} value={soilType} options={SOIL_TYPES} labels={SOIL_LABELS} onChange={setSoilType} />}
          </Field>
          <Field label="Sun exposure">
            {(id) => <Select id={id} value={sunExposure} options={SUN_EXPOSURES} labels={SUN_LABELS} onChange={setSunExposure} />}
          </Field>
          <Field label="Slope" hint="Steeper slopes get shorter cycles to prevent runoff.">
              {(id) => <Select id={id} value={slope} options={SLOPES} labels={SLOPE_LABELS} onChange={setSlope} />}
            </Field>
          </div>
        </Section>

        <Section title="Schedule">
        {(eligibleTargets.length > 0 || mirroring) && (
          <Field
            label="Waters with another zone"
            hint="Mirror a smart zone: this zone waters whenever that one does, at the same depth (sized to this zone's own nozzle and soil). Its own sensor, if any, still feeds its history."
          >
            {(id) => (
              <Select
                id={id}
                value={mirrorZoneId}
                options={['NONE', ...eligibleTargets.map((z) => z.id)]}
                labels={{ NONE: '— (own schedule)', ...Object.fromEntries(eligibleTargets.map((z) => [z.id, z.name])) }}
                onChange={setMirrorZoneId}
              />
            )}
          </Field>
        )}

        {mirroring && (
          <div className="grid cols-2">
            <Field
              label="Amount from target"
              hint={
                amountMode === 'AUTO'
                  ? `Matches ${mirrorTargetName ?? 'the target'}'s depth, sized to this zone's nozzle and soil.`
                  : `A set duration — best for drip. Runs this long whenever ${mirrorTargetName ?? 'the target'} waters.`
              }
            >
              {(id) => (
                <Select
                  id={id}
                  value={amountMode}
                  options={['AUTO', 'FIXED'] as const}
                  labels={{ AUTO: 'Automatic (match target)', FIXED: 'Fixed duration (drip)' }}
                  onChange={setAmountMode}
                />
              )}
            </Field>
            {amountMode === 'FIXED' && (
              <Field label="Duration on the target's days (min)" hint="Runs this long whenever the target waters.">
                {(id) => (
                  <TextInput
                    id={id}
                    inputMode="numeric"
                    value={fixedRuntime}
                    onChange={(e) => setFixedRuntime(e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>
        )}

        {!mirroring && (
          <div className="grid cols-2">
          <Field
            label="Scheduling"
            hint={
              scheduleMode === 'SMART'
                ? 'Smart: the soil-moisture model decides when and how much.'
                : 'Fixed: waters on the days below for a set time. Safety caps and pause always apply.'
            }
          >
            {(id) => (
              <Select
                id={id}
                value={scheduleMode}
                options={['SMART', 'FIXED'] as const}
                labels={{ SMART: 'Smart (weather-based)', FIXED: 'Fixed schedule' }}
                onChange={setScheduleMode}
              />
            )}
          </Field>
          {scheduleMode === 'FIXED' && (
            <Field label="Run time per watering (min)">
              {(id) => (
                <TextInput
                  id={id}
                  inputMode="numeric"
                  value={fixedMinutes}
                  onChange={(e) => setFixedMinutes(e.target.value)}
                />
              )}
            </Field>
          )}
          {scheduleMode === 'SMART' && (
            <Field
              label="Watering amount"
              hint={
                amountMode === 'AUTO'
                  ? 'Computed from the soil deficit and application rate.'
                  : 'A set duration — best for drip, where inches/hour is impractical to measure.'
              }
            >
              {(id) => (
                <Select
                  id={id}
                  value={amountMode}
                  options={['AUTO', 'FIXED'] as const}
                  labels={{ AUTO: 'Automatic (from soil)', FIXED: 'Fixed duration (drip)' }}
                  onChange={setAmountMode}
                />
              )}
            </Field>
          )}
          </div>
        )}

        {!mirroring && scheduleMode === 'SMART' && amountMode === 'FIXED' && (
          <div className="grid cols-2">
            <Field
              label="Watering duration (min)"
              hint="Runs this long whenever the soil model says the zone is due."
            >
              {(id) => (
                <TextInput
                  id={id}
                  inputMode="numeric"
                  value={fixedRuntime}
                  onChange={(e) => setFixedRuntime(e.target.value)}
                />
              )}
            </Field>
          </div>
        )}

        {!mirroring && scheduleMode === 'FIXED' && (
          <div className="grid cols-2">
            <Field label="Watering days">
              {(id) => (
                <Select
                  id={id}
                  value={fixedKind}
                  options={['ANY', 'SPECIFIC_DAYS', 'INTERVAL'] as const}
                  labels={{ ANY: 'Every day', SPECIFIC_DAYS: 'Specific weekdays', INTERVAL: 'Every N days' }}
                  onChange={setFixedKind}
                />
              )}
            </Field>
            {fixedKind === 'INTERVAL' && (
              <Field label="Water every (days)">
                {(id) => (
                  <TextInput
                    id={id}
                    inputMode="numeric"
                    value={fixedEveryN}
                    onChange={(e) => setFixedEveryN(e.target.value)}
                  />
                )}
              </Field>
            )}
            {fixedKind === 'SPECIFIC_DAYS' && (
              <div className="field">
                <label>Weekdays</label>
                <div className="chip-row" role="group" aria-label="Fixed schedule weekdays">
                  {WEEKDAY_LABELS.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      className={`chip${fixedWeekdays.has(i) ? ' on' : ''}`}
                      aria-pressed={fixedWeekdays.has(i)}
                      onClick={() =>
                        setFixedWeekdays((s) => {
                          const next = new Set(s);
                          if (next.has(i)) next.delete(i);
                          else next.add(i);
                          return next;
                        })
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="field">
              <label>Weather intelligence</label>
              <Switch checked={fixedSmartSkips} onChange={setFixedSmartSkips} label="Smart skips (rain, freeze, wind)" />
              <span className="hint">
                Off: runs regardless of weather — right for new seed that needs water even in drizzle.
              </span>
            </div>
          </div>
        )}

        </Section>

        {/*
          Always rendered, even while mirroring. Dropping the section entirely
          left the user hunting for a control that had silently vanished when
          they picked a mirror target; the rule is easier to accept when it
          says so, and says what to do instead.
        */}
        <Section title="New seed">
          {mirroring ? (
            <span className="hint">
              This zone waters with {mirrorTargetName ?? 'another zone'}, so it can't run its own new-seed program.
              Overseeding both? Unlink the mirror above, give each zone its own program, and re-link after it ends.
            </span>
          ) : (
            <div className="stack">
              <Switch
                checked={seedOn}
                onChange={setSeedOn}
                label="Mist this zone for germination"
                disabled={seedBlockedNote !== undefined}
              />
              <span className="hint">
                {seedBlockedNote ??
                  "Keeps a seedbed damp with short runs several times a day, whatever the weather. While it is on, this zone's normal schedule pauses, and it starts again by itself the day after the end date, so there is nothing to remember to turn off."}
              </span>

              {seedOn && (
                <>
                  <div className="grid cols-2">
                    <Field label="Minutes per misting" hint="Short is the point: 1–15 minutes.">
                      {(id) => (
                        <TextInput
                          id={id}
                          inputMode="numeric"
                          value={seedMinutes}
                          onChange={(e) => setSeedMinutes(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="Start date">
                      {(id) => (
                        <TextInput
                          id={id}
                          type="date"
                          value={seedStart}
                          onChange={(e) => setSeedStart(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field label="End date" hint="Required: the program always expires.">
                      {(id) => (
                        <TextInput id={id} type="date" value={seedEnd} onChange={(e) => setSeedEnd(e.target.value)} />
                      )}
                    </Field>
                  </div>

                  <div className="field">
                    <label>Misting times</label>
                    <div className="chip-row" role="group" aria-label="Misting schedule presets">
                      {SEED_TIME_PRESETS.map((preset) => (
                        <button
                          key={preset.label}
                          type="button"
                          className={`chip${seedTimes.join() === preset.times.join() ? ' on' : ''}`}
                          aria-pressed={seedTimes.join() === preset.times.join()}
                          onClick={() => setSeedTimes([...preset.times])}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <div className="chip-row">
                      {seedTimes.map((time, i) => (
                        <span key={`seed-time-${i}`} className="seed-time">
                          <TextInput
                            type="time"
                            aria-label={`Misting time ${i + 1}`}
                            value={time}
                            onChange={(e) =>
                              setSeedTimes((list) => list.map((t, j) => (j === i ? e.target.value : t)))
                            }
                          />
                          {seedTimes.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label={`Remove misting time ${i + 1}`}
                              onClick={() => setSeedTimes((list) => list.filter((_, j) => j !== i))}
                            >
                              Remove
                            </Button>
                          )}
                        </span>
                      ))}
                    </div>
                    {seedTimes.length < 6 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setSeedTimes((list) => [
                            ...list,
                            fmtHhmm(Math.min(23 * 60 + 30, seedMinuteOf(list[list.length - 1] ?? '09:00') + 120)),
                          ])
                        }
                      >
                        Add a time
                      </Button>
                    )}
                    <span className="hint">
                      At least {SEED_MIN_SPACING_MINUTES} minutes apart, in order through the day. About{' '}
                      {fmtInches(
                        ((seedTimes.length * (Number(seedMinutes) || 0)) / 60) *
                          (Number(nums.precipRateInPerHr) || zone.precipRateInPerHr),
                      )}{' '}
                      a day at this zone's application rate.
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </Section>

        {(mirroring || scheduleMode === 'SMART') && (
          <Section title="Soil moisture sensor">
            <div className="stack">
              <Field
                label="Soil moisture sensor"
                hint="A linked sensor's reading is the watering trigger; without one, the weather model estimates it."
              >
                {(id) => (
                  <Select
                    id={id}
                    value={sensorId}
                    options={['NONE', ...sensors.map((s) => s.id)]}
                    labels={{ NONE: 'None (weather model only)', ...Object.fromEntries(sensors.map((s) => [s.id, s.name])) }}
                    onChange={(v) => {
                      setSensorId(v);
                      if (v !== 'NONE' && moistLow.trim() === '' && moistHigh.trim() === '') {
                        const defaults = moistureBoundsPct(soilType, plantType);
                        setMoistLow(String(defaults.lowPct));
                        setMoistHigh(String(defaults.highPct));
                      }
                    }}
                  />
                )}
              </Field>
              {sensorId !== 'NONE' && (
                <>
                  <div className="grid cols-2">
                    <Field
                      label="Water at (%)"
                      hint="Reading when the zone should water — roughly when plants first show stress."
                    >
                      {(id) => (
                        <TextInput
                          id={id}
                          inputMode="decimal"
                          value={moistLow}
                          onChange={(e) => setMoistLow(e.target.value)}
                        />
                      )}
                    </Field>
                    <Field
                      label="Full (%)"
                      hint="Reading the day after a deep watering, once drained ≈ field capacity."
                    >
                      {(id) => (
                        <TextInput
                          id={id}
                          inputMode="decimal"
                          value={moistHigh}
                          onChange={(e) => setMoistHigh(e.target.value)}
                        />
                      )}
                    </Field>
                  </div>
                  <Field
                    label="Allowed depletion if the sensor goes stale"
                    error={fieldErrors.allowedDepletionFraction}
                    hint="Used only when the sensor has no recent reading; the Water-at % above is the trigger otherwise."
                  >
                    {(id, invalid) => (
                      <TextInput
                        id={id}
                        inputMode="decimal"
                        aria-invalid={invalid}
                        value={nums.allowedDepletionFraction}
                        onChange={(e) => setNum('allowedDepletionFraction', e.target.value)}
                      />
                    )}
                  </Field>
                </>
              )}
            </div>
          </Section>
        )}

        <Section
          title="Water model & hardware"
          collapsible
          summary={hardwareSummary}
        >
        <div className="grid cols-2">
          <Field
            label="Nozzle type"
            hint={
              zone.rachioNozzleName === 'UNRECOGNIZED'
                ? "Rachio reports “UNRECOGNIZED” — a head type its API can't identify (e.g. MP Rotator); set it manually."
                : zone.rachioNozzleName
                  ? `Rachio reports “${zone.rachioNozzleName}”.`
                  : undefined
            }
          >
            {(id) => <Select id={id} value={nozzleType} options={NOZZLE_TYPES} labels={NOZZLE_LABELS} onChange={setNozzleType} />}
          </Field>
          <Field
            label="Application rate (in/hr)"
            error={fieldErrors.precipRateInPerHr}
            hint={
              scheduleMode === 'FIXED' || amountMode === 'FIXED' ? (
                'Not used for run time while a fixed duration is set — only for the usage estimate.'
              ) : (
                <>
                  Typical for {NOZZLE_LABELS[nozzleType].toLowerCase()}: {nozzleDefault} in/hr.{' '}
                  {Number(nums.precipRateInPerHr) !== nozzleDefault && (
                    <button
                      type="button"
                      className="linklike"
                      onClick={() => setNum('precipRateInPerHr', String(nozzleDefault))}
                    >
                      Use typical
                    </button>
                  )}
                </>
              )
            }
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="decimal"
                aria-invalid={invalid}
                value={nums.precipRateInPerHr}
                onChange={(e) => setNum('precipRateInPerHr', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Root depth (in)"
            error={fieldErrors.rootDepthIn}
            hint="Turf 4–8 in · shrubs 12–24 in · trees 18–36 in."
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="decimal"
                aria-invalid={invalid}
                value={nums.rootDepthIn}
                onChange={(e) => setNum('rootDepthIn', e.target.value)}
              />
            )}
          </Field>
          {(mirroring || scheduleMode === 'SMART') && sensorId === 'NONE' && (
            <Field
              label="Allowed depletion (0–1, e.g. 0.5 = water at half dry)"
              error={fieldErrors.allowedDepletionFraction}
              hint="Fraction of available water to use before watering — the trigger. Defaults by plant."
            >
              {(id, invalid) => (
                <TextInput
                  id={id}
                  inputMode="decimal"
                  aria-invalid={invalid}
                  value={nums.allowedDepletionFraction}
                  onChange={(e) => setNum('allowedDepletionFraction', e.target.value)}
                />
              )}
            </Field>
          )}
          <Field
            label="Sprinkler efficiency (0–1, e.g. 0.75 = a quarter is lost)"
            error={fieldErrors.efficiency}
            hint="Sprays ~0.7, rotors ~0.75, drip ~0.9."
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="decimal"
                aria-invalid={invalid}
                value={nums.efficiency}
                onChange={(e) => setNum('efficiency', e.target.value)}
              />
            )}
          </Field>
          <Field label="Area (sq ft, optional)" error={fieldErrors.areaSqFt} hint="Only used for water-usage reporting.">
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.areaSqFt}
                onChange={(e) => setNum('areaSqFt', e.target.value)}
              />
            )}
          </Field>
        </div>

        {isDripNozzle && (
          <>
            <hr className="divider" style={{ margin: 0 }} />
            <div className="grid cols-2">
              <Field
                label="Drip coverage"
                hint="Emitters wet only part of the plant's canopy, so this zone holds a smaller water reservoir and waters in short, frequent runs. Pick the closest layout — it sets the coverage and a typical application rate."
              >
                {(id) => (
                  <Select
                    id={id}
                    value={dripLayout}
                    options={[...DRIP_LAYOUTS, 'CUSTOM']}
                    labels={{ ...DRIP_LAYOUT_LABELS, CUSTOM: 'Custom (set coverage below)' }}
                    onChange={(v) => applyDripLayout(v as DripLayout | 'CUSTOM')}
                  />
                )}
              </Field>
              {dripLayout === 'CUSTOM' ? (
                <Field
                  label="Wetted fraction (0–1, share of the canopy the emitters reach)"
                  hint="Fraction of the canopy the emitters actually wet. Lower = smaller reservoir, more frequent runs."
                >
                  {(id) => (
                    <TextInput
                      id={id}
                      inputMode="decimal"
                      value={wettedFraction}
                      onChange={(e) => setWettedFraction(e.target.value)}
                    />
                  )}
                </Field>
              ) : (
                <Field label="Coverage" hint="Set by the layout. Choose Custom to enter your own.">
                  {() => (
                    <p className="input-readout">
                      Wets ~{Math.round(Number(wettedFraction) * 100)}% of the canopy · {nums.precipRateInPerHr} in/hr
                    </p>
                  )}
                </Field>
              )}
            </div>
            {sensorId === 'NONE' && (
              <p className="hint" style={{ marginTop: '-0.25rem' }}>
                Without a moisture sensor these coverage numbers are estimates. For moisture-sensitive
                plantings, add a probe in the wetted zone or use a fixed watering duration.
              </p>
            )}
          </>
        )}
        </Section>

        {/* The card's own estimate, live, so an edit shows its effect without
            saving and going back to look. */}
        {liveEstimate && <div className="editor-estimate">{liveEstimate}</div>}

        <div className="actions">
          <Button busy={busy} onClick={() => void save()}>
            Save zone
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
